import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ms, s, vs } from '../../utils/responsive';
import { useAI } from '../../contexts/AIContext';
import { navigationRef } from '../../services/navigationService';


// Screens where Orion's floating button should be hidden so it doesn't
// compete with primary CTAs or distract the user during single-purpose
// flows. Mirrors FeedbackButton's exclusion list.
const EXCLUDED_SCREENS = new Set([
  // Auth + welcome flow
  'Splash',
  'TrustedPartyWelcome',
  'SignUp',
  'Login',
  'PrivacyPolicy',
  'TermsOfService',
  'SecretCodeRecipients',
  'MyPriorities',
  'EmailVerificationNotice',
  'EmailVerificationResult',
  // Invitation links: single-purpose, often opened signed out
  'AcceptInvitation',
  'FamilyInvite',
  'SecurityChooser',
  'TotpSetup',
  'TotpVerification',
  // Legacy onboarding flow (still used by v0 users)
  'Onboarding',
  'UserOnboarding',
  'AIOnboardingWelcome',
  'AIOnboardingTutorial',
  'AIOnboardingChat',
  'AIOnboardingPriorities',
  'AIOnboardingPaused',
  'WhatIsNext',
  // New wizard flow (Phase 1)
  'WizardProfile',
  'WizardHousehold',
  'WizardRolesIntro',
  'WizardProfessionals',
  'WizardDocuments',
  'WizardSuccessorSoftAsk',
  'WizardPrioritiesReveal',
]);

// Walk the potentially-nested navigator state to the deepest active route.
const getCurrentRouteName = (state: any): string | undefined => {
  if (!state) return undefined;
  const route = state.routes?.[state.index];
  if (route?.state) return getCurrentRouteName(route.state);
  return route?.name;
};

export const AIAssistantButton: React.FC = () => {
  const { toggleChat, isChatOpen } = useAI();
  const insets = useSafeAreaInsets();
  const [scaleAnim] = React.useState(new Animated.Value(1));
  const [currentRoute, setCurrentRoute] = React.useState<string | undefined>(undefined);

  // Track the active route so we can hide the button on excluded screens.
  // Pattern mirrors FeedbackButton — subscribe to navigationRef state
  // changes and recompute the deepest route name.
  React.useEffect(() => {
    if (navigationRef.isReady()) {
      setCurrentRoute(getCurrentRouteName(navigationRef.getRootState()));
    }
    const unsub = navigationRef.addListener('state', () => {
      setCurrentRoute(getCurrentRouteName(navigationRef.getRootState()));
    });
    return () => unsub();
  }, []);

  const handlePress = () => {
    // Animate button press
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.9,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();

    toggleChat();
  };

  if (isChatOpen) {
    // Hide button when chat is open
    return null;
  }

  if (currentRoute && EXCLUDED_SCREENS.has(currentRoute)) {
    // Hide on auth / onboarding / wizard screens so Orion doesn't
    // compete with the screen's primary CTA or distract the user.
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          // Sit above the home indicator rather than inside it. The
          // component is mounted at the app root inside SafeAreaProvider,
          // so a raw bottom offset lands in the system gesture area on
          // every modern iPhone. The floor keeps pre-notch devices where
          // they were.
          bottom: Math.max(insets.bottom, ms(10)) + ms(20),
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      <TouchableOpacity
        style={styles.button}
        onPress={handlePress}
        activeOpacity={0.8}
      >
        <Image
          source={require('../../assets/icons/chat.png')}
          style={styles.chatIcon}
          resizeMode="contain"
        />
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: ms(20),
    zIndex: 1000,
    elevation: 5,
  },
  button: {
    width: s(60),
    height: s(60),
    borderRadius: ms(30),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
  },
  chatIcon: {
    width: s(50),
    height: s(50),
  },
});
